"use client"

import * as React from "react"

import { Slot } from "@radix-ui/react-slot"
import { type VariantProps, cva } from "class-variance-authority"
import { cn } from "@/lib/util/utils"

/**
 * Utility for managing typography styles with variants.
 *
 * The `typographyVariants` object defines multiple visual styles for typography elements,
 * including headings, paragraphs, blockquotes, and more.
 */
const typographyVariants = cva("font-achiko", {
    variants: {
        variant: {
            h1: "scroll-m-20 text-xl md:text-4xl tracking-tight text-balance",
            h2: "scroll-m-20 text-lg md:text-3xl tracking-tight",
            h3: "scroll-m-20 text-md md:text-2lg tracking-tight",
            h4: "scroll-m-20 text-sm md:text-md tracking-tight",
            h5: "scroll-m-20 text-base md:text-sm tracking-tight",
            h6: "scroll-m-20 text-base tracking-tight",
            p: "text-md leading-5",
            lead: "text-muted-foreground text-xl",
            large: "text-lg tracking-tight",
            small: "text-sm leading-none font-medium",
            caption: "text-xs text-muted-foreground",
            muted: "text-muted-foreground text-sm",
            blockquote: "mt-6 border-l-2 pl-6 italic",
            inlineCode: "bg-muted relative rounded px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
            codeBlock: "bg-muted relative rounded-md p-4 font-mono text-sm overflow-x-auto",
            kbd: "rounded border bg-muted px-1.5 py-0.5 font-mono text-xs font-medium",
            mark: "bg-yellow-200 text-black px-1 rounded-sm",
            list: "my-6 ml-6 list-disc [&>li]:mt-2",
            table: "my-6 w-full overflow-auto border [&_th]:font-bold [&_td]:font-normal [&_th]:text-left [&_td]:text-left",
        },
        align: {
            left: "text-left",
            center: "text-center",
            right: "text-right",
            justify: "text-justify",
        },
        tone: {
            default: "text-green-main",
            muted: "text-muted-foreground",
            destructive: "text-destructive",
            primary: "text-primary",
            secondary: "text-secondary",
            accent: "text-muted-foreground",
        },
        weight: {
            normal: "font-normal",
            medium: "font-medium",
            semibold: "font-semibold",
            bold: "font-bold",
        },
        transform: {
            none: "",
            uppercase: "uppercase",
            lowercase: "lowercase",
            capitalize: "capitalize",
        },
    },
    defaultVariants: {
        variant: "p",
        align: "left",
        tone: "default",
        weight: "normal",
        transform: "none",
    },
})

/**
 * Props for the `Typography` component.
 *
 * @property variant - The typography variant (e.g., `h1`, `p`, `blockquote`).
 * @property align - Text alignment. Can be `left`, `center`, `right`, or `justify`.
 * @property tone - The tone of the text. Can be `default`, `muted`, `destructive`, `primary`, `secondary`, or `accent`.
 * @property weight - The font weight. Can be `normal`, `medium`, `semibold`, or `bold`.
 * @property transform - Text transformation. Can be `none`, `uppercase`, `lowercase`, or `capitalize`.
 * @property asChild - If `true`, renders the typography element as a child element using `Slot`.
 */
export interface TypographyProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof typographyVariants> {
    /**
     * If `true`, renders the typography element as a child element using `Slot`.
     */
    asChild?: boolean
}

/**
 * Maps typography variants to their corresponding HTML elements.
 */
const variantMap = {
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
    h6: "h6",
    p: "p",
    lead: "p",
    large: "div",
    small: "small",
    caption: "span",
    muted: "p",
    blockquote: "blockquote",
    inlineCode: "code",
    codeBlock: "pre",
    kbd: "kbd",
    mark: "mark",
    list: "ul",
    table: "div",
} as const

/**
 * A flexible and customizable typography component.
 *
 * The `Typography` component provides a consistent way to style text elements
 * with support for variants, alignment, tone, weight, and text transformation.
 *
 * ---
 *
 * ## Features
 *
 * - 🎨 **Customizable:** Supports multiple typography variants and styles.
 * - ♿ **Accessible:** Built with semantic HTML elements for better accessibility.
 * - 🔄 **Flexible:** Can render as a custom element using the `asChild` prop.
 *
 * ---
 *
 * ## Example
 *
 * ```tsx
 * import { Typography } from "@/components/ui/typography"
 *
 * export function Example() {
 *   return (
 *     <Typography variant="h1" align="center" tone="primary">
 *       Welcome to shop
 *     </Typography>
 *   )
 * }
 * ```
 *
 * @param props - The props for the `Typography` component.
 * @param props.variant - The typography variant (e.g., `h1`, `p`, `blockquote`).
 * @param props.align - Text alignment. Can be `left`, `center`, `right`, or `justify`.
 * @param props.tone - The tone of the text. Can be `default`, `muted`, `destructive`, `primary`, `secondary`, or `accent`.
 * @param props.weight - The font weight. Can be `normal`, `medium`, `semibold`, or `bold`.
 * @param props.transform - Text transformation. Can be `none`, `uppercase`, `lowercase`, or `capitalize`.
 * @param props.asChild - If `true`, renders the typography element as a child element using `Slot`.
 * @param ref - A ref to the underlying HTML element.
 */
const Typography = React.forwardRef<HTMLElement, TypographyProps>(
    ({ className, variant = "p", align, tone, weight, transform, asChild = false, ...props }, ref) => {
        const Comp: React.ElementType = asChild
            ? Slot
            : (variant && variantMap[variant as keyof typeof variantMap]) || "p"

        return (
            <Comp
                ref={ref}
                className={cn(typographyVariants({ variant, align, tone, weight, transform }), className)}
                {...props}
            />
        )
    },
)

Typography.displayName = "Typography"

export { Typography }
